import { useContext } from 'react';
import { MenuItem, MenuDivider } from '@szhsin/react-menu';
import { Box } from '@apia/theme';
import { FileElementContext } from '../FileElement';
import DocumentInfoModal from '../../../../common/modals/DocumentInfoModal';
import { OpenModalContext } from '../../../../common/modals/OpenModalContext';
import { formatMessage } from '../../../../../../utils/util';
import UploaderContext from '../../../../../common/uploader/context';
import { useAppSelector } from '../../../../../../hooks/hooks';
import { isOneClickUploadEnabled } from '../../../../../common/uploader/UploaderApi';

const RenderMenu = () => {
  const {
    file,
    isDisabled,
    isReadonly,
    noDownload,
    noErase,
    noLock,
    noModify,
    noSign,
    noVersion,
    noVerifySign,
    onCheckSignature,
    onDownloadFile,
    onEditFile,
    onLockFile,
    onOneClickUpload,
    onRemoveFile,
    onTranslateFile,
    onSignFile,
    onVersion,
  } = useContext(FileElementContext);

  const { api } = useContext(UploaderContext);
  const translationsCount = useAppSelector(
    (global) =>
      Object.values(
        global.fileUploadSlice.uploaders[api.id].translatedFiles[file.docId] ??
          {},
      ).length,
  );

  const canEdit = file.canEdit && !window.IN_MONITOR;

  const shouldShowLockButton = !noLock && !isReadonly && !isDisabled && canEdit;
  const shouldShowVersionButton =
    canEdit &&
    Number(file.docId) >= 0 &&
    !noVersion &&
    (!isOneClickUploadEnabled(api.configurationObject.oneClickUpload) ||
      onOneClickUpload);
  const shouldShowRemoveButton =
    !noErase && !isReadonly && !isDisabled && canEdit;
  const shouldShowSignButton = !noSign && !isReadonly && !isDisabled && canEdit;
  const shouldShowTranslateButton =
    !isReadonly && !isDisabled && canEdit && onTranslateFile;

  return (
    <Box className="fileUploaderMenu">
      {(file.canEdit || file.canRead) && (
        <DocumentInfoModal file={file}>
          <OpenModalContext.Consumer>
            {({ show, isLoading }) => (
              <MenuItem
                onClick={(e) => {
                  // eslint-disable-next-line no-param-reassign
                  e.stopPropagation = true;
                  show();
                }}
                disabled={isLoading}
                className="information"
              >
                {window.BTN_FILE_INFO_LBL}
              </MenuItem>
            )}
          </OpenModalContext.Consumer>
        </DocumentInfoModal>
      )}

      {parseInt(file?.docId, 10) > 0 &&
        !isReadonly &&
        !isDisabled &&
        canEdit &&
        !noModify && (
          <MenuItem
            onClick={(e) => {
              // eslint-disable-next-line no-param-reassign
              e.stopPropagation = true;
              onEditFile(file);
            }}
            className="editFile"
          >
            {window.LBL_EDIT}
          </MenuItem>
        )}

      {!noDownload && (
        <MenuItem
          onClick={(e) => {
            // eslint-disable-next-line no-param-reassign
            e.stopPropagation = true;
            onDownloadFile(file);
          }}
          disabled={!file.canRead && !file.canEdit}
          className="download"
        >
          {window.BTN_FILE_DOWNLOAD_LBL}
        </MenuItem>
      )}

      {!noVerifySign && Number(file?.docId) > 0 && (
        <MenuItem
          onClick={(e) => {
            // eslint-disable-next-line no-param-reassign
            e.stopPropagation = true;
            if (onCheckSignature) onCheckSignature(file);
          }}
          disabled={!file.canRead && !file.canEdit}
          className="verifySigns"
        >
          {window.BTN_VERIFY_FRM_SIGN}
        </MenuItem>
      )}

      {(shouldShowLockButton ||
        shouldShowVersionButton ||
        shouldShowRemoveButton) && <MenuDivider />}

      {shouldShowLockButton && parseInt(file?.docId, 10) > 0 && (
        <MenuItem
          checked={file?.isLocked}
          type="checkbox"
          disabled={String(file?.canEdit) === 'false'}
          onClick={(e) => {
            // eslint-disable-next-line no-param-reassign
            e.stopPropagation = true;
            // eslint-disable-next-line no-param-reassign
            e.keepOpen = true;
            onLockFile(file);
          }}
          className={file.lockingUser ? 'unlock' : 'lock'}
        >
          {file.lockingUser
            ? formatMessage(window.LBL_DOC_LOCKED_BY, {
                TOK1: file.lockingUser,
              })
            : window.LBL_LOCK}
        </MenuItem>
      )}

      {shouldShowVersionButton && (
        <MenuItem
          onClick={(e) => {
            // eslint-disable-next-line no-param-reassign
            e.stopPropagation = true;
            if (isOneClickUploadEnabled(api.configurationObject.oneClickUpload))
              onOneClickUpload?.(file);
            else onVersion?.(file);
          }}
          className="version"
        >
          {window.UPLOAD_NEW_VERSION}
        </MenuItem>
      )}

      {shouldShowRemoveButton && (
        <MenuItem
          onClick={(e) => {
            // eslint-disable-next-line no-param-reassign
            e.stopPropagation = true;
            onRemoveFile(file);
          }}
          className="delete"
        >
          {window.BTN_FILE_ERASE_LBL}
        </MenuItem>
      )}

      {(shouldShowSignButton || shouldShowTranslateButton) && <MenuDivider />}

      {shouldShowSignButton && (
        <MenuItem
          checked={file.isMarkedToSign}
          type="checkbox"
          onClick={(e) => {
            // eslint-disable-next-line no-param-reassign
            e.stopPropagation = true;
            if (onSignFile) onSignFile(file);
          }}
          className="markToSign"
        >
          {window.BTN_MARK_FRM_TO_SIGN}
        </MenuItem>
      )}

      {shouldShowTranslateButton && (
        <MenuItem
          disabled={!file.canRead && !canEdit}
          onClick={(e) => {
            // eslint-disable-next-line no-param-reassign
            e.stopPropagation = true;
            if (onTranslateFile) onTranslateFile(file);
          }}
          className="translate"
        >
          {window.BTN_TRANSLATE}{' '}
          {translationsCount > 0 && `(${translationsCount})`}
        </MenuItem>
      )}
    </Box>
  );
};

export default RenderMenu;
